import React, { Component } from 'react';
import { renderCoverBlob } from '../utils/imageResize';
import {
    ANCHO_SALIDA, ALTO_SALIDA, ANCHO_MINIMO, ALTO_MINIMO,
} from '../utils/cartelera';

/**
 * Elegir una imagen de la cartelera, encuadrarla y subirla.
 *
 * Sirve para las TRES imágenes que tiene la pantalla —la foto de un bloque, el fondo de la
 * pantalla y el fondo propio de un bloque— porque las tres son lo mismo: una imagen vertical
 * que va a ocupar el televisor entero. Lo único que cambia es a dónde se sube, y eso lo
 * decide el que lo usa (`onSubir`).
 *
 * El televisor está en VERTICAL y su caja no cambia nunca de forma, así que acá hay UNA sola
 * medida de salida (1080x1920) y el preview muestra exactamente lo que se va a proyectar. Es
 * la diferencia con la pantalla de portada del home, donde hay que simular un segundo recorte
 * porque la caja del hero cambia con el ancho de la ventana.
 *
 * Lo que igual hace falta es el PUNTO FOCAL: la foto que trae el operador casi nunca es 9:16,
 * y `cover` tiene que decidir qué mitad tira. Sin esto, una foto apaisada de un torneo se
 * recorta al centro y quedan las caras afuera.
 */

const acotar = (v) => Math.min(1, Math.max(0, v));

const medirImagen = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('imagen ilegible')); };
    img.src = url;
});

class TvImageUploader extends Component {
    constructor(props) {
        super(props);
        this.state = {
            seleccion: null,  // { file, url, width, height, focusX, focusY, aviso }
            subiendo: false,
            error: null,
        };
        this.input = null;
        this.arrastrando = false;
    }

    componentWillUnmount() {
        this.desmontado = true;
        if (this.state.seleccion) URL.revokeObjectURL(this.state.seleccion.url);
    }

    onArchivoElegido = async (event) => {
        const file = event.target.files && event.target.files[0];
        // Permitir cancelar el diálogo sin romper nada.
        event.target.value = '';
        if (!file) return;

        if (!file.type || !file.type.startsWith('image/')) {
            this.setState({ error: 'El archivo no es una imagen.' });
            return;
        }
        if (file.type === 'image/svg+xml') {
            this.setState({ error: 'Los SVG no sirven acá: subí una foto o una placa (JPG, PNG o WebP).' });
            return;
        }

        let dim;
        try {
            dim = await medirImagen(file);
        } catch (e) {
            this.setState({ error: 'No se pudo leer la imagen. Probá con otro archivo.' });
            return;
        }

        // Se miran las DOS dimensiones: recortar "cover" a 1080x1920 necesita ancho >= 1080 Y
        // alto >= 1920. Con sólo el ancho, una panorámica pasa el chequeo y después sale chica.
        if (dim.width < ANCHO_MINIMO || dim.height < ALTO_MINIMO) {
            this.setState({
                error: `"${file.name}" mide ${dim.width}x${dim.height}px y el mínimo es ${ANCHO_MINIMO}x${ALTO_MINIMO}px. En una pantalla de 50 pulgadas, más chica que eso se ve pixelada.`,
            });
            return;
        }

        const aviso = (dim.width < ANCHO_SALIDA || dim.height < ALTO_SALIDA)
            ? `La imagen mide ${dim.width}x${dim.height}px y para llenar el televisor hacen falta ${ANCHO_SALIDA}x${ALTO_SALIDA}px. Se sube igual, en la resolución que dé el original.`
            : null;

        if (this.state.seleccion) URL.revokeObjectURL(this.state.seleccion.url);
        this.setState({
            error: null,
            seleccion: {
                file,
                url: URL.createObjectURL(file),
                width: dim.width,
                height: dim.height,
                focusX: 0.5,
                focusY: 0.5,
                aviso,
            },
        });
    };

    // --- punto focal ---------------------------------------------------------------------

    moverFoco = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        this.setState((prev) => ({
            seleccion: {
                ...prev.seleccion,
                focusX: acotar((event.clientX - rect.left) / rect.width),
                focusY: acotar((event.clientY - rect.top) / rect.height),
            },
        }));
    };

    onFocoDown = (event) => {
        // Sólo botón izquierdo y puntero primario: con el derecho se abre el menú contextual,
        // el `pointerup` no llega y el foco queda siguiendo al mouse sin que nadie apriete nada.
        if (event.button !== 0 || event.isPrimary === false) return;
        if (this.state.subiendo || !this.state.seleccion) return;
        this.arrastrando = true;
        if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
            event.currentTarget.setPointerCapture(event.pointerId);
        }
        this.moverFoco(event);
    };

    onFocoMove = (event) => {
        if (!this.arrastrando || this.state.subiendo) return;
        this.moverFoco(event);
    };

    onFocoUp = () => { this.arrastrando = false; };

    // --- subida --------------------------------------------------------------------------

    subir = async () => {
        const sel = this.state.seleccion;
        if (!sel || this.state.subiendo) return;
        this.setState({ subiendo: true, error: null });

        try {
            const generado = await renderCoverBlob(sel.file, {
                width: ANCHO_SALIDA,
                height: ALTO_SALIDA,
                focusX: sel.focusX,
                focusY: sel.focusY,
            });
            const resultado = await this.props.onSubir(generado.blob);
            if (this.desmontado) return;
            URL.revokeObjectURL(sel.url);
            this.setState({ seleccion: null, subiendo: false });
            if (this.props.onSubida) this.props.onSubida(resultado);
        } catch (error) {
            if (this.desmontado) return;
            this.setState({ subiendo: false, error: error.message || 'No se pudo subir la imagen.' });
        }
    };

    cancelar = () => {
        if (this.state.seleccion) URL.revokeObjectURL(this.state.seleccion.url);
        this.setState({ seleccion: null, error: null });
    };

    render() {
        const { seleccion, subiendo, error } = this.state;
        const { imageUrl, etiqueta } = this.props;

        return (
            <div>
                <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    ref={(el) => { this.input = el; }}
                    onChange={this.onArchivoElegido}
                />

                {error && <div className="alert alert-danger py-2 px-3 mb-2">{error}</div>}

                {!seleccion && (
                    <button className="btn btn-sm btn-outline-primary" onClick={() => this.input && this.input.click()}>
                        <i className="material-icons" style={{ fontSize: 16, verticalAlign: 'text-bottom' }}>upload</i>
                        {' '}{imageUrl ? `Cambiar ${etiqueta || 'imagen'}` : `Subir ${etiqueta || 'imagen'}`}
                    </button>
                )}

                {seleccion && (
                    <div className="border rounded p-3 mt-2" style={{ background: '#fafbfc' }}>
                        <div className="d-flex" style={{ gap: 16, flexWrap: 'wrap' }}>
                            {/* El preview tiene la forma EXACTA del televisor. El recuadro
                                punteado es lo que entra; lo de afuera se descarta. */}
                            <div
                                onPointerDown={this.onFocoDown}
                                onPointerMove={this.onFocoMove}
                                onPointerUp={this.onFocoUp}
                                onPointerCancel={this.onFocoUp}
                                style={{
                                    position: 'relative',
                                    width: 135,
                                    height: 240,
                                    flex: '0 0 auto',
                                    overflow: 'hidden',
                                    borderRadius: 4,
                                    background: '#111',
                                    cursor: subiendo ? 'progress' : 'crosshair',
                                    touchAction: 'none',
                                }}
                                title="Arrastrá para elegir qué parte de la foto se ve"
                            >
                                <img
                                    src={seleccion.url}
                                    alt="Vista previa"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        objectPosition: `${seleccion.focusX * 100}% ${seleccion.focusY * 100}%`,
                                        pointerEvents: 'none',
                                    }}
                                />
                            </div>

                            <div style={{ flex: '1 1 260px', minWidth: 240 }}>
                                <div className="mb-2" style={{ fontSize: 13 }}>
                                    <strong>{seleccion.file.name}</strong>
                                    <span className="text-muted"> · {seleccion.width}x{seleccion.height}px</span>
                                </div>
                                <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                                    Arrastrá sobre la miniatura para elegir qué parte de la foto queda
                                    dentro de la pantalla. Lo que ves ahí es exactamente lo que se
                                    proyecta.
                                </p>
                                {seleccion.aviso && (
                                    <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: 12 }}>
                                        {seleccion.aviso}
                                    </div>
                                )}
                                <button className="btn btn-sm btn-primary mr-2" onClick={this.subir} disabled={subiendo}>
                                    {subiendo ? 'Subiendo…' : 'Subir'}
                                </button>
                                <button className="btn btn-sm btn-outline-secondary" onClick={this.cancelar} disabled={subiendo}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default TvImageUploader;
